import { Typography, Box, IconButton } from "@mui/material";
import { useState } from "react";
import { useNavigate } from "react-router";
import { useAuth } from "@/context/AuthContext";
import { UserRoles } from "@/api/user.service";
import { EmailConfigPage } from "@/components/config/EmailConfigPage";
import { ThemeConfigPage } from "@/components/config/ThemeConfigPage";
import { SplashConfigPage } from "@/components/config/SplashConfigPage";
import ClientSelectModal from "@/components/ClientSelectModal";
import { getClients, type Client } from "@/api/client.service";
import { Business, Settings, KeyboardArrowRight, NavigateBefore } from "@mui/icons-material";
import { alpha } from "@mui/material/styles";

const AdminConfigPlaceholder = ({ title, onClick }: { title: string; onClick: () => void }) => (
  <Box
    onClick={onClick}
    sx={{
      mb: 3,
      borderRadius: 4,
      background: (theme) => alpha(theme.palette.background.paper, 0.75),
      backdropFilter: 'blur(10px)',
      border: '1px solid',
      borderColor: 'divider',
      boxShadow: (theme) => `0 4px 20px ${alpha(theme.palette.primary.main, 0.06)}`,
      cursor: 'pointer',
      transition: 'box-shadow 0.2s ease',
      '&:hover': { boxShadow: (theme) => `0 6px 28px ${alpha(theme.palette.primary.main, 0.10)}`, background: (theme) => alpha(theme.palette.background.paper, 0.85) },
    }}
  >
    <Box sx={{ px: 3, py: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <Box>
        <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: 'text.primary' }}>{title}</Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>Clique para selecionar um cliente</Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32, borderRadius: 2, bgcolor: (theme) => alpha(theme.palette.text.secondary, 0.08), color: 'text.secondary', flexShrink: 0 }}>
        <KeyboardArrowRight fontSize="small" />
      </Box>
    </Box>
  </Box>
);

export const ConfigPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [clientSelectTarget, setClientSelectTarget] = useState<'theme' | 'splash' | null>(null);
  const [themeClient, setThemeClient] = useState<Client | null>(null);
  const [splashClient, setSplashClient] = useState<Client | null>(null);
  const [clients, setClients] = useState<Client[]>([]);

  const handleOpenModal = async (target: 'theme' | 'splash') => {
    if (clients.length === 0) {
      try {
        const data = await getClients({ offset: 0, limit: 100, active: true });
        setClients(data.clients);
      } catch {
        setClients([]);
      }
    }
    setClientSelectTarget(target);
  };

  const handleSelectClient = (client: Client) => {
    if (clientSelectTarget === 'theme') setThemeClient(client);
    else if (clientSelectTarget === 'splash') setSplashClient(client);
    setClientSelectTarget(null);
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Header Section */}
      <Box
        sx={{
          mb: 4,
          p: 3,
          borderRadius: 4,
          background: (theme) => alpha(theme.palette.background.paper, 0.75),
          backdropFilter: 'blur(10px)',
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: (theme) => `0 4px 20px ${alpha(theme.palette.primary.main, 0.06)}`,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <IconButton
          onClick={() => navigate(-1)}
          sx={{
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
            borderRadius: 2,
            flexShrink: 0,
            '&:hover': { bgcolor: (theme) => alpha(theme.palette.primary.main, 0.15) },
          }}
        >
          <NavigateBefore />
        </IconButton>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 48,
            height: 48,
            borderRadius: 3,
            bgcolor: (theme) => alpha(theme.palette.text.secondary, 0.1),
            color: 'text.secondary',
            flexShrink: 0,
          }}
        >
          <Settings sx={{ fontSize: 26 }} />
        </Box>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 900, color: 'text.primary', letterSpacing: '-0.02em' }}>
            Configurações
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.25 }}>
            Gerencie as preferências e integrações do sistema.
          </Typography>
        </Box>
      </Box>

      {user?.role == UserRoles.Admin && (
        <>
          <EmailConfigPage />

          {splashClient ? (
            <SplashConfigPage
              key={splashClient.id}
              clientId={splashClient.id}
              clientName={splashClient.name}
              initialSplashUrl={splashClient.splash_url}
              initialSplashUrlDark={splashClient.splash_url_dark}
              defaultOpen
              onChangeClient={() => handleOpenModal('splash')}
            />
          ) : (
            <AdminConfigPlaceholder title="Arte de Carregamento" onClick={() => handleOpenModal('splash')} />
          )}

          {themeClient ? (
            <ThemeConfigPage
              key={themeClient.id}
              clientId={themeClient.id}
              clientName={themeClient.name}
              defaultOpen
              onChangeClient={() => handleOpenModal('theme')}
            />
          ) : (
            <AdminConfigPlaceholder title="Configuração de Tema" onClick={() => handleOpenModal('theme')} />
          )}
        </>
      )}

      <ClientSelectModal
        open={clientSelectTarget !== null}
        clients={clients}
        onSelect={handleSelectClient}
      />

      {user?.role == UserRoles.Cliente && user.loggedClient && (
        <>
          <SplashConfigPage />
          <ThemeConfigPage />
        </>
      )}

      {/* Fallback for Cliente + no logged User */}
      {user?.role === UserRoles.Cliente && !user.loggedClient && (
        <Box
          sx={{
            mt: 2,
            p: 5,
            borderRadius: 4,
            background: (theme) => alpha(theme.palette.background.paper, 0.75),
            backdropFilter: 'blur(10px)',
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: (theme) => `0 4px 20px ${alpha(theme.palette.primary.main, 0.06)}`,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 56,
              height: 56,
              borderRadius: 3,
              mb: 3,
              bgcolor: (theme) => alpha(theme.palette.text.secondary, 0.12),
              color: 'text.secondary',
            }}
          >
            <Business sx={{ fontSize: 32 }} />
          </Box>

          <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary', mb: 1 }}>
            Vínculo de Empresa Ausente
          </Typography>

          <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6, maxWidth: 400 }}>
            Seu usuário ainda não está vinculado a nenhuma empresa.
            Entre em contato com um <strong>administrador</strong> para habilitar as configurações de cliente.
          </Typography>
        </Box>
      )}
    </Box>
  );
};